import React, { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { resourceCache } from 'use-async-resource';
import { AddActionComponent } from '../components/AddActionComponent';
import { CustomTable } from '../components/CustomTable';
import { PaginationComponent } from '../components/Pagination';
import { TableComponentProps, TableInnerProps, User } from '../models/table-models';
import {
    fetchDistributorDevices,
    useDeleteUser,
    useGetDeletedUsers,
    useGetUsers,
    useRestoreDeletedUser,
} from '../utils/users-hooks';
import { usersTableHeaders, usersWithDeviceTableHeaders } from '../utils/Enums/TableHeaders';
import { usePagination } from '../utils/pagination-hooks';
import { useTableData } from '../utils/data-hooks';
import { AddEditUser } from './AddEditUser';
import { useTranslation } from 'react-i18next';
import { NoDataComponent } from '../components/NoDataComponent';
import dateFormat from 'dateformat';
import { PageSizeComponent } from '../components/PageSizeComponent';
import { faArrowUp, faExclamationTriangle, faSignInAlt, faSnowflake } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AuthContext } from '../context/AuthContext';
import { Role } from '../utils/Enums/Roles';
import {toast} from "react-toastify";
import {CustomToastContent} from "../components/CustomToastContent";
import {toastOptionsTopRight} from "../utils/Enums/ToastOptions";
import { DistributorLocationHistoryModal } from './DistributorLocationHistoryModal';
import { DistributorDevice } from '../models/distributor-location-models';
import { CustomButton } from '../components/CustomButton';
import { FleetVehicle } from '../models/fleet-models';
import { fetchFleetVehicles } from '../utils/fleet-hooks';
import { useNavigate } from 'react-router-dom';

const clearTableCache = async () => {
    resourceCache(useGetDeletedUsers).clear();
    resourceCache(useGetUsers).clear();
};

export const Users = ({ filter, pagination }: TableComponentProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { hasPermission } = useContext(AuthContext);
    const [getUsers, fetchNewUsers] = useTableData<User[]>(useGetUsers, pagination, filter);
    const [getFrozenUsers, fetchNewDeletedUsers] = useTableData<User[]>(useGetDeletedUsers, pagination, filter);

    const refetchUsersHook = () => {
        clearTableCache().then();
        fetchNewUsers({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter,
        });
    };
    const frozenUsersPagination = usePagination(100);

    const refetchFrozeUsersHook = () => {
        clearTableCache().then();
        fetchNewDeletedUsers({
            'pager.page': frozenUsersPagination.currentPage,
            'pager.itemsPerPage': frozenUsersPagination.pageSize,
            pagination: frozenUsersPagination,
            filter,
        });
    };

    return (
        <>
            <div className='d-flex w-100 justify-content-between mb-3'>
                <AddActionComponent
                    accessActionRoles={[]}
                    title={t('addUser')}
                    addComponent={AddEditUser}
                    refetchEntitiesHook={refetchUsersHook}
                ></AddActionComponent>
            </div>
            <Suspense fallback={<Spinner animation='border' />}>
                <UsersInner pagination={pagination} getEntities={getUsers} refetchEntitiesHook={refetchUsersHook} />
            </Suspense>
            {hasPermission([Role.ADMIN]) && (
                <Suspense fallback={<Spinner animation='border' />}>
                    <FrozenUsersInner
                        pagination={frozenUsersPagination}
                        getEntities={getFrozenUsers}
                        refetchEntitiesHook={refetchFrozeUsersHook}
                    />
                </Suspense>
            )}
        </>
    );
};

const UsersInner = ({ getEntities, pagination, refetchEntitiesHook }: TableInnerProps<User>) => {
    const { pager, items } = getEntities();
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const rolesTranslation = useTranslation('translation', { keyPrefix: 'multiselect' });
    const [locationHistoryUser, setLocationHistoryUser] = useState<User>();
    const [devices, setDevices] = useState<{ [userId: number]: DistributorDevice }>({});
    const { hasPermission, isImpersonating, loggedUser } = useContext(AuthContext);
    const [impersonationTarget, setImpersonationTarget] = useState<User>();
    const [vehiclesByUser, setVehiclesByUser] = useState<{ [userId: number]: FleetVehicle }>({});

    // Телефонът не стои при потребителя, а при локациите му, затова се дърпа отделно за показаната
    // страница. Ключът е низ от идентификаторите: масивът от use-async-resource е нов обект при всяко
    // рендиране и би завъртял заявката в кръг.
    const userIdsKey = useMemo(() => items.map(({ id }) => id).filter(Boolean).join(','), [items]);

    useEffect(() => {
        if (!userIdsKey) {
            setDevices({});
            return;
        }

        let cancelled = false;
        fetchDistributorDevices(userIdsKey.split(',').map(Number))
            .then((result) => {
                if (!cancelled) {
                    setDevices(Object.fromEntries(result.map((device) => [device.userId, device])));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDevices({});
                }
            });

        return () => {
            cancelled = true;
        };
    }, [userIdsKey]);

    // Колата, на която човекът е шофьор в GPS Areon в момента. Тегли се наново с всяка страница, за да
    // се види смяна, направена от редакцията на потребителя. Без GPS Areon колоната просто е празна.
    useEffect(() => {
        if (!userIdsKey) {
            return;
        }

        let cancelled = false;
        fetchFleetVehicles({ skipErrorInterceptor: true })
            .then((vehicles) => {
                if (!cancelled && Array.isArray(vehicles)) {
                    setVehiclesByUser(Object.fromEntries(
                        vehicles.filter((vehicle) => vehicle.driverUserId).map((vehicle) => [vehicle.driverUserId, vehicle]),
                    ));
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setVehiclesByUser({});
                }
            });

        return () => {
            cancelled = true;
        };
    }, [userIdsKey]);

    const vehicleCell = (userId?: number) => {
        const vehicle = userId ? vehiclesByUser[userId] : undefined;
        return vehicle ? <span className='text-nowrap' title={vehicle.label}>{vehicle.plateNumber ?? vehicle.deviceIdentifier}</span> : '-';
    };

    // В колоната стои сдвоеният телефон от профила. Последно видяният от локациите служи за сверяване:
    // разминаване значи, че човекът работи от друг апарат, а не от този, който му е записан.
    const deviceCell = (userId?: number, pairedDeviceId?: string) => {
        const seen = userId ? devices[userId] : undefined;
        const seenId = seen?.mobileDeviceId;
        const seenAt = seen ? `последно видян: ${seenId ?? '-'} (${dateFormat(seen.lastSeenAt, 'dd/mm/yyyy HH:MM')})` : undefined;

        if (!pairedDeviceId) {
            if (!seenId) {
                return '-';
            }

            return (
                <span
                    className='font-monospace text-nowrap'
                    style={{ fontSize: '0.85em', color: '#997404' }}
                    title={`не е сдвоен, ${seenAt}`}
                >
                    {seenId}
                </span>
            );
        }

        const isMismatched = !!seenId && seenId.toLowerCase() !== pairedDeviceId.toLowerCase();

        return (
            <span
                className={`font-monospace text-nowrap ${isMismatched ? 'text-danger' : ''}`}
                style={{ fontSize: '0.85em' }}
                title={[isMismatched ? 'работи от друг телефон' : undefined, seenAt].filter(Boolean).join(', ') || undefined}
            >
                {pairedDeviceId}
                {isMismatched && <> <FontAwesomeIcon icon={faExclamationTriangle} /></>}
            </span>
        );
    };

    const useActionHook = async (fields: User) => {
        await useDeleteUser(fields);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('userFrozen')]}/>, toastOptionsTopRight);
    };
    return (
        <>
            {pager.totalCount > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: usersWithDeviceTableHeaders,
                            items: items.map(
                                ({ id, username, roles, town, group, name, phone, email, creation_time, comment, mobileDeviceId }) => [
                                    username,
                                    roles.map(rolesTranslation.t),
                                    group?.mainGroup ? group.mainGroup : t('allGroups'),
                                    group?.subGroup ?group.subGroup : t('allGroups'),
                                    town,
                                    name,
                                    phone,
                                    email,
                                    dateFormat(creation_time, 'dd/mm/yyyy'),
                                    comment,
                                    deviceCell(id, mobileDeviceId),
                                    vehicleCell(id),
                                ],
                            ),
                        }}
                        accessActionRoles={[]}
                        accessEditRoles={[]}
                        editComponent={AddEditUser}
                        actionHook={useActionHook}
                        actionText={'freezeUser'}
                        actionIcon={faSnowflake}
                        deleteButtonText={'freeze'}
                        tableData={items}
                        viewComponentObject={setLocationHistoryUser}
                        rowExtraAction={(user) => hasPermission([Role.ADMIN]) && !isImpersonating && user.id !== loggedUser?.id ? (
                            <CustomButton
                                className='mx-1 px-2 color-main text-nowrap'
                                icon={faSignInAlt}
                                iconClass='fa-fw'
                                title={t('impersonate')}
                                tooltipTitle={t('impersonate')}
                                onClick={() => setImpersonationTarget(user)}
                            />
                        ) : null}
                        {...{ refetchEntitiesHook }}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                    {locationHistoryUser && (
                        <DistributorLocationHistoryModal
                            user={locationHistoryUser}
                            onHide={() => setLocationHistoryUser(undefined)}
                        />
                    )}
                    {impersonationTarget && (
                        <ImpersonationModal user={impersonationTarget} onHide={() => setImpersonationTarget(undefined)} />
                    )}
                </>
            ) : (
                <NoDataComponent noDataText={t('activeUsers')}></NoDataComponent>
            )}
        </>
    );
};

const ImpersonationModal = ({ user, onHide }: { user: User; onHide: () => void }) => {
    const { impersonateUser } = useContext(AuthContext);
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async () => {
        if (!user.id) return;

        setIsLoading(true);
        setHasError(false);
        const status = await impersonateUser(user.id);
        setIsLoading(false);

        if (status >= 200 && status < 300) {
            onHide();
            const destination = user.roles.some((role) =>
                role === Role.ADMIN || role === Role.COORDINATOR || role === Role.DISTRIBUTOR || role === Role.INSTALLER
            )
                ? '/dashboard'
                : user.roles.includes(Role.WAREHOUSE)
                    ? '/warehouses'
                    : user.roles.includes(Role.FINANCIAL)
                        ? '/invoices'
                        : '/no-access';
            navigate(destination, { replace: true });
        } else {
            setHasError(true);
        }
    };

    return (
        <Modal show onHide={() => !isLoading && onHide()} centered size='sm' backdrop={isLoading ? 'static' : true}>
            <Modal.Header closeButton={!isLoading}>
                <Modal.Title>{t('impersonate')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className='mb-3'>{t('impersonateAs', { name: user.name || user.username })}</p>
                {hasError && <div className='text-danger small mt-2'>{t('impersonationFailed')}</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant='secondary' onClick={onHide} disabled={isLoading}>{t('cancel')}</Button>
                <Button onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? <Spinner animation='border' size='sm' /> : t('confirm')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

const FrozenUsersInner = ({ getEntities, pagination, refetchEntitiesHook }: TableInnerProps<User>) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const rolesTranslation = useTranslation('translation', { keyPrefix: 'multiselect' });

    const { pager, items } = getEntities();

    const useActionHook = async (fields: User) => {
        await useRestoreDeletedUser(fields);
        await clearTableCache();
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('userUnfrozen')]}/>, toastOptionsTopRight);
    };
    return (
        <>
            <h3 className='align-items-start w-100 mb-3'>{t('deletedUsers')}</h3>
            <div className='align-items-start w-100 mb-3'>
                <PageSizeComponent pagination={pagination} itemsToShow={[5, 10, 15]} />
            </div>
            {pager.totalCount > 0 ? (
                <>
                    <CustomTable
                        columns={{
                            headings: usersTableHeaders,
                            items: items.map(
                                ({ username, roles, group, town, name, phone, email, creation_time, comment }) => [
                                    username,
                                    roles.map(rolesTranslation.t),
                                    group.mainGroup,
                                    group.subGroup,
                                    town,
                                    name,
                                    phone,
                                    email,
                                    dateFormat(creation_time, 'dd/mm/yyyy'),
                                    comment,
                                ],
                            ),
                        }}
                        actionHook={useActionHook}
                        actionText={'restoreUser'}
                        actionIcon={faArrowUp}
                        deleteButtonText={'restore'}
                        tableData={items}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                </>
            ) : (
                <NoDataComponent noDataText={t('deletedUsers')}></NoDataComponent>
            )}
        </>
    );
};
